import { queryOptions, useQuery, UseQueryResult } from '@tanstack/react-query';

import {
  getApiClient,
  is403Error,
  overrideOrganizationHeader,
} from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { ReportingTrialStatusResponse } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { REPORTING_TRIAL_ENABLED } from './reportingTrialEnabled';

const REPORTING_TRIAL_STATUS_QUERY_KEY = 'reporting-trial-status';

export const getReportingTrialStatusQueryKey = (organizationId?: string) =>
  [REPORTING_TRIAL_STATUS_QUERY_KEY, organizationId] as const;

/** Skip authz 403 retries; allow a couple of attempts for transient failures. */
export const shouldRetryReportingTrialStatusQuery = (
  failureCount: number,
  error: unknown
): boolean => !is403Error(error) && failureCount < 2;

export const getReportingTrialStatusQueryOptions = (
  organizationId: string | undefined,
  enabled: boolean
) =>
  queryOptions({
    queryKey: getReportingTrialStatusQueryKey(organizationId),
    queryFn: async () => {
      const { data } =
        await getApiClient().costReport.getReportingMonetizationStatus({
          headers: overrideOrganizationHeader(organizationId),
        });
      return data;
    },
    enabled: makeIsEnabled(enabled, !!organizationId, REPORTING_TRIAL_ENABLED),
    // Mutations invalidate this key; skip focus/mount refetch to avoid 403 flap
    // (CSU-6357). Reconnect still refetches (QueryClient default).
    retry: shouldRetryReportingTrialStatusQuery,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

type useReportingTrialStatusQueryArgs = {
  data?: ReportingTrialStatusResponse;
  isLoading: boolean;
  error: unknown;
  refetch: UseQueryResult<ReportingTrialStatusResponse, unknown>['refetch'];
};

export const useReportingTrialStatusQuery = ({
  enabled,
  organizationId: organizationIdOverride,
}: {
  enabled: boolean;
  organizationId?: string;
}): useReportingTrialStatusQueryArgs => {
  const { isReady: isOrganizationReady, currentOrganization } =
    useOrganizations();

  const organizationId = organizationIdOverride ?? currentOrganization?.id;

  const { data, isLoading, error, refetch } = useQuery(
    getReportingTrialStatusQueryOptions(
      organizationId,
      enabled && isOrganizationReady
    )
  );

  return { data, isLoading, error, refetch };
};
